import { useCallback, useEffect } from 'react';
import { useSender } from './useSender';
import { useSignalRStore } from './useSignalRStore';
import useWidgetDataAttr from './useWidgetDataAttr';

export interface UseSignalRReturn {
  isConnected: boolean;
  subscribe: (
    methodName: string,
    callback: (...args: unknown[]) => void,
  ) => void;
  unsubscribe: (
    methodName: string,
    callback?: (...args: unknown[]) => void,
  ) => void;
}

const url = `${import.meta.env.VITE_API_URL}/chat`;

export const useSignalR = (): UseSignalRReturn => {
  const {
    companyId,
    widgetId: channelIdentity,
    location,
  } = useWidgetDataAttr();
  const { senderId } = useSender();

  const {
    isConnected,
    actions: {
      connect: storeConnect,
      subscribe: storeSubscribe,
      unsubscribe: storeUnsubscribe,
    },
  } = useSignalRStore();

  // Auto-connect on mount and join the group
  useEffect(() => {
    const connectAndJoinGroup = async () => {
      if (!url || !location || !companyId || !channelIdentity || !senderId) {
        return;
      }

      try {
        const groupName = `${companyId}_livechat_${senderId}_${channelIdentity}`;
        await storeConnect(url, location, groupName);
      } catch (error) {
        console.error('Failed to connect and join group:', error);
      }
    };

    connectAndJoinGroup();
  }, [location, storeConnect, companyId, channelIdentity, senderId]);

  // Subscribe function to listen to SignalR events
  const subscribe = useCallback(
    (methodName: string, callback: (...args: unknown[]) => void) => {
      storeSubscribe(methodName, callback);
    },
    [storeSubscribe],
  );

  // Unsubscribe function to stop listening to SignalR events
  const unsubscribe = useCallback(
    (methodName: string, callback?: (...args: unknown[]) => void) => {
      storeUnsubscribe(methodName, callback);
    },
    [storeUnsubscribe],
  );

  return {
    isConnected,
    subscribe,
    unsubscribe,
  };
};
